<script setup lang="ts">
import type {
  InsightConfig,
  InsightConfigJsonField,
  InsightConfigMeta,
} from '../../../models/insightConfig.types'
import { INSIGHT_CONFIG_JSON_FIELD_SPECS } from '../../../service/insightConfig'

import { CONFIG_FIELD_I18N_KEYS } from './configI18n'

import { useI18n } from '@/shared/i18n'
import { JsonField } from '@/shared/ui/json-field'
import { Select } from '@/shared/ui/select'

const form = defineModel<InsightConfig>('form', { required: true })
const json = defineModel<Record<InsightConfigJsonField, string>>('json', { required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  errors: Record<string, string[]>
  disabled?: boolean
}>()
const { t } = useI18n()
const fields = [
  'column_definitions',
  'hidden_dimensions',
  'tab_definitions',
  'visualization_config',
] as const

function errorFor(field: string): string {
  return (props.errors[field] ?? [])
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
}
</script>

<template>
  <fieldset class="config-section grid gap-md min-w-0 p-lg border border-border rounded-lg">
    <legend>{{ t('insights.sysadmin.config.section.visualization') }}</legend>
    <Select
      :model-value="form.content_type"
      :label="t('insights.sysadmin.config.fields.contentType')"
      :options="meta.content_type_choices"
      :disabled="disabled"
      @update:model-value="$event !== null && (form = { ...form, content_type: String($event) })"
    />
    <JsonField
      v-for="field in fields"
      :key="field"
      :model-value="json[field]"
      :spec="INSIGHT_CONFIG_JSON_FIELD_SPECS[field]"
      :label="t(`insights.sysadmin.config.fields.${CONFIG_FIELD_I18N_KEYS[field]}`)"
      :description="t(`insights.sysadmin.config.jsonDescription.${CONFIG_FIELD_I18N_KEYS[field]}`)"
      :error-message="errorFor(field)"
      :null-label="t('insights.sysadmin.config.setNull')"
      :default-label="t('insights.sysadmin.config.useDefault')"
      :disabled="disabled"
      @update:model-value="json = { ...json, [field]: $event }"
    />
  </fieldset>
</template>

<style scoped>
.config-section legend {
  padding-inline: var(--ui-space-xs);
  font-weight: 600;
}
</style>
